import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  EventNote as NoteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";

export default function NursingNotes({ patient, encounter }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const [createNoteOpen, setCreateNoteOpen] = useState(false);

  const {
    data: notesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["nursing-notes", patient?.id, encounter?.id],
    queryFn: () => {
      let url = `${process.env.REACT_APP_API_URL}/nursing-notes/`;
      const params = new URLSearchParams();
      if (patient?.id) params.append("patient", patient.id);
      if (encounter?.id) params.append("encounter", encounter.id);
      if (params.toString()) url += `?${params.toString()}`;
      
      return axios
        .get(url, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => res.data);
    },
    enabled: !!token && (!!patient?.id || !!encounter?.id),
  });

  const notes = notesData?.results || notesData || [];

  const getNoteTypeColor = (type) => {
    const colors = {
      general: "default",
      care: "primary",
      medication: "secondary",
      vital: "info",
      patient_concern: "warning",
      family_communication: "success",
      other: "default",
    };
    return colors[type] || "default";
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || t("nurse.errors.loadFailed", "Failed to load nursing notes.")}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          {t("nurse.nursingNotes", "Nursing Notes")} ({notes.length})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateNoteOpen(true)}
        >
          {t("nurse.addNote", "Add Note")}
        </Button>
      </Box>

      {notes.length === 0 ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => setCreateNoteOpen(true)}>
              {t("nurse.createOne", "Create One")}
            </Button>
          }
        >
          {t("nurse.noNursingNotes", "No nursing notes found.")}
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {notes.map((note) => (
            <Card key={note.id} variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Chip
                    label={note.note_type_display || note.note_type}
                    size="small"
                    color={getNoteTypeColor(note.note_type)}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {moment(note.note_date).format("YYYY-MM-DD HH:mm")} - {note.nurse_display}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                  {note.content}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <NursingNoteCreate
        open={createNoteOpen}
        onClose={() => {
          setCreateNoteOpen(false);
          queryClient.invalidateQueries(["nursing-notes"]);
        }}
        patient={patient}
        encounter={encounter}
      />
    </Box>
  );
}

function NursingNoteCreate({ open, onClose, patient, encounter }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    patient: patient?.id || "",
    encounter: encounter?.id || "",
    note_type: "general",
    content: "",
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open) {
      setFormData({
        patient: patient?.id || "",
        encounter: encounter?.id || "",
        note_type: "general",
        content: "",
      });
      setErrors({});
    }
  }, [open, patient, encounter]);

  const createNote = useMutation({
    mutationFn: (data) =>
      axios.post(`${process.env.REACT_APP_API_URL}/nursing-notes/`, data, {
        headers: { Authorization: `Token ${token}` },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["nursing-notes"]);
      onClose();
    },
    onError: (error) => {
      console.error("Failed to create nursing note:", error);
      setErrors(error.response?.data || { non_field_errors: "Failed to create note." });
    },
  });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setErrors({ ...errors, [field]: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.patient) {
      newErrors.patient = t("nurse.errors.patientRequired", "Patient is required.");
    }
    if (!formData.content.trim()) {
      newErrors.content = t("nurse.errors.contentRequired", "Content is required.");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    createNote.mutate(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("nurse.addNursingNote", "Add Nursing Note")}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {createNote.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.non_field_errors || createNote.error?.message || t("nurse.errors.genericError", "An error occurred.")}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="note-type-label">{t("nurse.noteType", "Note Type")}</InputLabel>
                <Select
                  labelId="note-type-label"
                  id="note-type-select"
                  value={formData.note_type}
                  label={t("nurse.noteType", "Note Type")}
                  onChange={handleChange("note_type")}
                >
                  <MenuItem value="general">{t("nurse.noteTypeGeneral", "General Note")}</MenuItem>
                  <MenuItem value="care">{t("nurse.noteTypeCare", "Care Plan Note")}</MenuItem>
                  <MenuItem value="medication">{t("nurse.noteTypeMedication", "Medication Note")}</MenuItem>
                  <MenuItem value="vital">{t("nurse.noteTypeVital", "Vital Signs Note")}</MenuItem>
                  <MenuItem value="patient_concern">{t("nurse.noteTypePatientConcern", "Patient Concern")}</MenuItem>
                  <MenuItem value="family_communication">{t("nurse.noteTypeFamily", "Family Communication")}</MenuItem>
                  <MenuItem value="other">{t("nurse.noteTypeOther", "Other")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label={t("nurse.content", "Note Content")}
                value={formData.content}
                onChange={handleChange("content")}
                error={!!errors.content}
                helperText={errors.content}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={createNote.isPending}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={createNote.isPending ? <CircularProgress size={20} /> : <AddIcon />}
            disabled={createNote.isPending || !patient}
          >
            {createNote.isPending ? t("common.saving", "Saving...") : t("common.save", "Save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
